import { baseApi } from "../baseApi";
import { pick } from "../util";
import type { Broker, CreateBrokerInput, UpdateBrokerInput } from "../types";

const BROKER_FIELDS = `
  id
  name
  code
  companyName
  email
  phone
  commissionRate
  referralCode
  addressLine1
  city
  state
  country
  postalCode
  payoutAccount
  status
  notes
  createdAt
  updatedAt
`;

export const brokerApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getBrokers: builder.query<Broker[], void>({
      query: () => ({
        body: {
          query: `
            query Brokers {
              brokers {
                ${BROKER_FIELDS}
              }
            }
          `,
        },
      }),

      transformResponse: (res: unknown) => pick<Broker[]>(res, "brokers"),

      providesTags: (result) =>
        result
          ? [
              ...result.map((b) => ({ type: "Broker" as const, id: b.id })),
              { type: "Broker" as const, id: "LIST" },
            ]
          : [{ type: "Broker" as const, id: "LIST" }],
    }),

    getBroker: builder.query<Broker, string>({
      query: (id) => ({
        body: {
          query: `
            query Broker($id: String!) {
              broker(id: $id) {
                ${BROKER_FIELDS}
              }
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) => pick<Broker>(res, "broker"),

      providesTags: (_result, _err, id) => [{ type: "Broker", id }],
    }),

    createBroker: builder.mutation<Broker, CreateBrokerInput>({
      query: (input) => ({
        body: {
          query: `
            mutation CreateBroker($input: CreateBrokerInput!) {
              createBroker(input: $input) {
                ${BROKER_FIELDS}
              }
            }
          `,
          variables: { input },
        },
      }),

      transformResponse: (res: unknown) => pick<Broker>(res, "createBroker"),

      invalidatesTags: [{ type: "Broker", id: "LIST" }],
    }),

    updateBroker: builder.mutation<
      Broker,
      { id: string; input: UpdateBrokerInput }
    >({
      query: ({ id, input }) => ({
        body: {
          query: `
            mutation UpdateBroker($id: String!, $input: UpdateBrokerInput!) {
              updateBroker(id: $id, input: $input) {
                ${BROKER_FIELDS}
              }
            }
          `,
          variables: { id, input },
        },
      }),

      transformResponse: (res: unknown) => pick<Broker>(res, "updateBroker"),

      invalidatesTags: (_result, _err, { id }) => [
        { type: "Broker", id },
        { type: "Broker", id: "LIST" },
      ],
    }),

    deleteBroker: builder.mutation<boolean, string>({
      query: (id) => ({
        body: {
          query: `
            mutation DeleteBroker($id: String!) {
              deleteBroker(id: $id)
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) => pick<boolean>(res, "deleteBroker"),

      invalidatesTags: (_result, _err, id) => [
        { type: "Broker", id },
        { type: "Broker", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetBrokersQuery,
  useLazyGetBrokersQuery,
  useGetBrokerQuery,
  useLazyGetBrokerQuery,
  useCreateBrokerMutation,
  useUpdateBrokerMutation,
  useDeleteBrokerMutation,
} = brokerApi;
